import { useState } from 'react'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useLocation, useNavigate } from 'react-router-dom'
import apiClient from './apiClient'

function useRegisterService() {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    middleName: '',
    lastName: '',
  })
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  const [formDataError, setFormDataError] = useState({
    emailError: '',
    firstNameError: '',
    lastNameError: '',
    roleError: '',
  })

  const handleChange = (e) => {
    if (!e || !e.target) {
      console.error('Event or target is undefined:', e)
      return
    }

    const { name, value } = e.target
    setFormDataError({ ...formDataError, [`${name}Error`]: '' })
    setFormData({ ...formData, [name]: value })
  }

  const handleRoleDropdownChange = (e) => {
    if (!e || !e.target) {
      console.error('Event or target is undefined:', e)
      return
    }
    const { name, value } = e.target
    setFormDataError({ ...formDataError, [`${name}Error`]: '' })
    setFormData({ ...formData, [name]: value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      let hasError = false

      const requiredFieldsForTutor = ['email', 'firstName', 'lastName']
      const requiredFieldsForCounselor = ['email', 'firstName', 'lastName']
      const optionalFieldsForTutor = ['middleName']
      const optionalFieldsForCounselor = ['middleName']

      const requiredFields =
        formData.role === 'Tutor'
          ? [...requiredFieldsForTutor, ...optionalFieldsForTutor]
          : [...requiredFieldsForCounselor, ...optionalFieldsForCounselor]

      requiredFields.forEach((fieldName) => {
        if (
          typeof formData[fieldName] === 'string' &&
          !formData[fieldName].trim() &&
          !optionalFieldsForTutor.includes(fieldName)
        ) {
          setFormDataError((prevState) => ({
            ...prevState,
            [`${fieldName}Error`]: 'Required',
          }))
          hasError = true
        }
      })

      if (hasError) return

      const filteredFormData = requiredFields.reduce((acc, field) => {
        acc[field] = formData[field] ?? ''
        return acc
      }, {})

      // Set endpoint based on role
      let endpoint = ''
      if (formData.role === 'Tutor') {
        endpoint = '/Account/Tutor-Register'
      } else if (formData.role === 'Counselor') {
        endpoint = '/Account/Counselor-Register'
      } else if (formData.role === 'Mentor') {
        endpoint = '/Account/Mentor-Register'
      } else {
        console.error('Invalid role provided:', formData.role)
        return
      }

      const response = await apiClient.post(endpoint, filteredFormData, {
        setLoading: setIsLoading,
      })

      if (response && response.status === 200) {
        toast.success('Registration submitted — check your email for a link to set your password', {
          autoClose: 5000,
          style: { background: 'lightgreen', color: 'white' },
        })

        // Admin registrations must return to the admin portal picker. The actor
        // registration is a public onboarding flow for the new actor; it must
        // not replace the currently authenticated admin session with that actor's
        // login page.
        if (location.state?.fromAdmin) {
          navigate(location.state.returnTo || '/multiLogin', { replace: true })
        } else {
          navigate('/login', {
            state: {
              successMessage:
                'Registration submitted — check your email for a link to set your password',
            },
          })
        }
      }
    } catch (error) {
      console.error('Error during registration:', error)
      // Batch-07: surface role-registration conflicts to the user.
      const code = error?.response?.rawData?.code || error?.response?.data?.code
      const map = {
        TUTOR_EMAIL_ALREADY_EXISTS: 'This email is already registered as a tutor.',
        COUNSELOR_EMAIL_ALREADY_EXISTS: 'This email is already registered as a counselor.',
        MENTOR_EMAIL_ALREADY_EXISTS: 'This email is already registered as a mentor.',
        TUTOR_EMAIL_ROLE_CONFLICT: 'This email is already registered under a different role.',
        COUNSELOR_EMAIL_ROLE_CONFLICT: 'This email is already registered under a different role.',
        MENTOR_EMAIL_ROLE_CONFLICT: 'This email is already registered under a different role.',
      }
      toast.error(map[code] || 'Registration failed. Please try again.', {
        autoClose: 5000,
        style: { background: 'lightcoral', color: 'white' },
      })
    } finally {
      setIsLoading(false)
    }
  }

  // const handleSubmit = async (e) => {
  //   e.preventDefault()
  //   setIsLoading(true)

  //   try {
  //     let hasError = false

  //     // Define required fields for each role
  //     const requiredFieldsForTutor = ['email', 'firstName', 'lastName']

  //     const requiredFieldsForCounselor = ['email', 'firstName', 'lastName']

  //     // Optional fields for Tutor
  //     const optionalFieldsForTutor = ['middleName']
  //     const optionalFieldsForCounselor = ['middleName']

  //     // Determine required fields based on role
  //     const requiredFields =
  //       formData.role === 'Tutor'
  //         ? [...requiredFieldsForTutor, ...optionalFieldsForTutor]
  //         : [...requiredFieldsForCounselor, ...optionalFieldsForCounselor]

  //     // Validate required fields
  //     requiredFields.forEach((fieldName) => {
  //       if (
  //         typeof formData[fieldName] === 'string' &&
  //         !formData[fieldName].trim() &&
  //         !optionalFieldsForTutor.includes(fieldName)
  //       ) {
  //         setFormDataError((prevState) => ({
  //           ...prevState,
  //           [`${fieldName}Error`]: 'Required', // Change the message here
  //         }))

  //         hasError = true
  //       }
  //     })

  //     if (hasError) {
  //       console.log('Validation errors:', formDataError)
  //       return
  //     }

  //     // Prepare filtered form data based on role
  //     const filteredFormData = requiredFields.reduce((acc, field) => {
  //       acc[field] = formData[field] !== undefined ? formData[field] : ''
  //       return acc
  //     }, {})

  //     // Send request based on role
  //     let endpoint = ''

  //     if (formData.role === 'Tutor') {
  //       endpoint = `${process.env.REACT_APP_BASE_URL_API}/Account/Tutor-Register`
  //     } else if (formData.role === 'Counselor') {
  //       endpoint = `${process.env.REACT_APP_BASE_URL_API}/Account/Counselor-Register`
  //     } else if (formData.role === 'Mentor') {
  //       endpoint = `${process.env.REACT_APP_BASE_URL_API}/Account/Mentor-Register`
  //     } else {
  //       console.error('Invalid role provided:', formData.role)
  //     }

  //     const response = await fetch(endpoint, {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify(filteredFormData),
  //     })

  //     if (response.ok) {
  //       const data = await response.json()
  //       console.log('Registration Successful:', data)
  //       toast.success('Registration submitted — check your email for a link to set your password', {
  //         autoClose: 5000,
  //         style: { background: 'lightgreen', color: 'white' },
  //       })

  //     } else {
  //       console.log('Registration failed with status:', response.status)
  //       try {
  //         const contentType = response.headers.get('content-type')
  //         if (contentType && contentType.includes('application/json')) {
  //           const errorData = await response.json()
  //           console.error('Registration Error Data:', errorData)
  //         } else {
  //           const errorText = await response.text()
  //           console.error('Registration Error Text:', errorText)
  //         }
  //       } catch (error) {
  //         console.error('Error parsing error response:', error)
  //       }
  //       toast.error('This User is already Exist', {
  //         autoClose: 5000,
  //         style: { background: 'lightcoral', color: 'white' },
  //       })
  //     }
  //   } catch (error) {
  //     console.error('Unexpected Error:', error)
  //   } finally {
  //     setIsLoading(false)
  //   }
  // }

  return {
    formData,
    formDataError,
    isLoading,
    handleSubmit,
    handleChange,
    handleRoleDropdownChange,
    setFormDataError,
    setFormData,
  }
}

export default useRegisterService
